import React from 'react';
import ResizeBoxesStep from '../shared/ResizeBoxesStep';
import ScaleStep from '../shared/ScaleStep';
import ShowScaleContoursStep from '../shared/ShowScaleContoursStep';
import StatusStep from '../shared/StatusStep';
import TagsStep from '../shared/TagsStep';
import WizardShell from '../shared/WizardShell';
import { figureUpdateInputs, parseIntOrNull } from '../shared/wizardUtils';
import useWizardFlow from '../shared/useWizardFlow';
import DataStep from './DataStep';
import ShowContoursStep from './ShowContoursStep';

const STEPS = [
  'data',
  'tags',
  'resize_boxes',
  'show_contours',
  'scale',
  'show_scale_contours',
  'status'
];
const STEP_LABELS = {
  data: 'Basic Data',
  tags: 'Tags',
  resize_boxes: 'Resize Boxes',
  show_contours: 'Contours',
  scale: 'Scale',
  show_scale_contours: 'Scale Contour',
  status: 'Status'
};

const initialForm = (figure) => ({
  identifier: figure?.identifier || '',
  siteId: String(figure?.siteId ?? ''),
  percentageScale: figure?.percentageScale ? `1:${figure.percentageScale}` : '',
  pageSize: String(figure?.pageSize ?? ''),
  selectedTagIds: figure?.tags?.map((tag) => tag.id) || [],
  verified: Boolean(figure?.verified)
});

const createSavers = ({ onUpdateFigure, onUpdateTags, onUpdateScale }) => ({
  data: (values) =>
    onUpdateFigure({
      identifier: values.identifier,
      siteId: parseIntOrNull(values.siteId)
    }),
  tags: (values, data) => onUpdateTags(data.tagIds ?? values.selectedTagIds),
  scale: (values, data) => onUpdateScale(data),
  status: (values) => onUpdateFigure({ verified: values.verified })
});

export default function SizeFigureWizard({
  step,
  setStep,
  figure,
  sites,
  tags,
  onCancel,
  onFinish,
  loading,
  ...handlers
}) {
  const hasScale = Boolean(figure?.scale);
  const steps = STEPS.filter((name) => name !== 'show_scale_contours' || hasScale);
  const { formData, updateField, handleNext } = useWizardFlow({
    steps,
    step,
    setStep,
    initialData: initialForm(figure),
    savers: createSavers(handlers)
  });

  const handleFinishFigures = async (figures) => {
    await handlers.onUpdateFigures(figureUpdateInputs(figures));
    handleNext();
  };

  const renderStep = () => {
    switch (step) {
      case 'data':
        return (
          <DataStep
            figure={figure}
            state={formData}
            sites={sites}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      case 'tags':
        return (
          <TagsStep
            tags={tags}
            selectedTagIds={formData.selectedTagIds}
            onSelectTag={(ids) => updateField('selectedTagIds', ids)}
            onNext={handleNext}
            onCancel={onCancel}
          />
        );
      case 'resize_boxes':
        return (
          <ResizeBoxesStep
            resource={figure}
            sites={sites}
            onFinishFigures={handleFinishFigures}
            onCancel={onCancel}
          />
        );
      case 'show_contours':
        return (
          <ShowContoursStep
            figure={figure}
            onUpdateContour={handlers.onUpdateContour}
            onNext={handleNext}
            onCancel={onCancel}
          />
        );
      case 'scale':
        return (
          <ScaleStep
            resource={figure}
            state={formData}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      case 'show_scale_contours':
        return (
          <ShowScaleContoursStep
            resource={figure}
            onUpdateScaleContour={handlers.onUpdateScaleContour}
            onNext={handleNext}
            onCancel={onCancel}
          />
        );
      case 'status':
        return (
          <StatusStep
            resource={figure}
            state={formData}
            updateField={updateField}
            onNext={handleNext}
            onCancel={onCancel}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <WizardShell
      steps={steps}
      labels={STEP_LABELS}
      step={step}
      setStep={setStep}
      onFinish={onFinish}
    >
      {renderStep()}
    </WizardShell>
  );
}
